import { Component, OnInit, OnDestroy, ViewChild, ElementRef, NgZone, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { WebcamImage, WebcamInitError, WebcamModule, WebcamUtil } from 'ngx-webcam';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, Subject } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LoadingService } from 'src/app/core/services/loading.service';
import { ToastService } from 'src/app/shared/utils/toast.service';
import jsPDF from 'jspdf';

@Component({
  selector: 'app-document-scanner',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    WebcamModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatInputModule,
    MatFormFieldModule,
    MatTooltipModule,
    TranslateModule
  ],
  templateUrl: './document-scanner.component.html',
  styleUrl: './document-scanner.component.scss'
})
export class DocumentScannerComponent implements OnInit, OnDestroy {
  @ViewChild('fileNameInput') fileNameInput: ElementRef;
  @ViewChild('webcam') webcam: any;
  
  // Basic orientation detection
  public isPortraitOrientation: boolean = window.innerHeight > window.innerWidth;
  
  // Camera options
  public showWebcam = true;
  public allowCameraSwitch = true;
  public multipleWebcamsAvailable = false;
  public deviceId: string;
  public availableCameras: MediaDeviceInfo[] = [];

  // True once the camera stream is actually playing. Until then we cover the
  // raw <video> with an opaque placeholder, because the Android/iOS WebView
  // paints an ugly native "play" button over a video that has no active stream
  // yet (e.g. while the permission prompt is up, or when permission is denied).
  public cameraActive = false;
  
  // Store event handler reference for cleanup
  private viewportHandler: (() => void) | null = null;
  
  // Mobile scroll indicator
  public showScrollHint = false;
  
  // Camera switching state
  private isSwitchingCamera = false;

  public videoOptions: MediaTrackConstraints = {
    facingMode: 'environment', // Prefer back camera
    width: { min: 640, ideal: 1280, max: 1920 },
    height: { min: 480, ideal: 720, max: 1080 }
  };
  public errors: WebcamInitError[] = [];

  // Trigger subject for webcam image capture
  private trigger: Subject<void> = new Subject<void>();

  // Captured images
  public capturedImages: WebcamImage[] = [];
  public fileName = 'scanned_document.pdf';
  public isCapturing = false;
  public isMobile = false;

  // Services
  private loadingService = inject(LoadingService);
  private toastService = inject(ToastService);
  private translate = inject(TranslateService);
  private zone = inject(NgZone);
  
  constructor(
    public dialogRef: MatDialogRef<DocumentScannerComponent>
  ) {}
  ngOnInit(): void {
    // Detect mobile device
    this.isMobile = window.innerWidth < 768 || 
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Set up available cameras
    this.setupCameras();
    
    // Listen for orientation changes
    window.addEventListener('resize', this.handleScreenResize.bind(this));
    
    // Handle Android browser bottom navigation bar changes
    this.handleViewportChanges();
    
    // Add scroll listener to hide hint when user scrolls
    if (this.isMobile) {
      window.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
    }
    
    // Handle browser back button to prevent Auth0 errors
    this.handleBrowserBackButton();

    // Start watching for the camera stream becoming active so we can drop the
    // placeholder that hides the WebView's native video play-button overlay.
    this.watchForActiveCamera();
  }  ngOnDestroy(): void {
    // Remove event listeners
    window.removeEventListener('resize', this.handleScreenResize.bind(this));
    window.removeEventListener('popstate', this.onBrowserBackButton.bind(this));
    window.removeEventListener('scroll', this.onScroll.bind(this));
    
    // Remove viewport handler if it exists
    if (this.viewportHandler && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.viewportHandler);
    }
    
    // Clean up body classes
    document.body.classList.remove('has-browser-ui');
  }
  
  private handleScreenResize(): void {
    // Update mobile detection
    this.isMobile = window.innerWidth < 768 || 
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Update orientation detection
    this.isPortraitOrientation = window.innerHeight > window.innerWidth;
    
    // Handle viewport changes for Android browsers
    this.handleViewportChanges();
  }
  
  private handleViewportChanges(): void {
    // Handle Android browser bottom navigation bar showing/hiding
    if (this.isMobile && window.visualViewport) {
      const viewportHandler = () => {
        const viewportHeight = window.visualViewport!.height;
        const windowHeight = window.innerHeight;
        
        // If visual viewport is significantly smaller than window height,
        // it means browser UI (bottom nav) is visible
        const hasBottomUI = (windowHeight - viewportHeight) > 50;
        
        // Update CSS custom property for dynamic adjustments
        document.documentElement.style.setProperty(
          '--viewport-height', 
          `${viewportHeight}px`
        );
        
        // Check if scrolling is needed (content height > viewport height)
        const contentHeight = document.querySelector('.scanner-content')?.scrollHeight || 0;
        this.showScrollHint = hasBottomUI && contentHeight > viewportHeight;
        
        // Add class to body for CSS targeting
        if (hasBottomUI) {
          document.body.classList.add('has-browser-ui');
        } else {
          document.body.classList.remove('has-browser-ui');
        }
      };
      
      this.viewportHandler = viewportHandler;
      window.visualViewport.addEventListener('resize', viewportHandler);
      viewportHandler(); // Initial call
    }
  }
  
  private handleBrowserBackButton(): void {
    // Add a history entry to handle back button
    history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', this.onBrowserBackButton.bind(this));
  }
  
  private onBrowserBackButton(event: PopStateEvent): void {
    // Prevent the default back navigation
    event.preventDefault();
    
    // Push state again to stay on the current page
    history.pushState(null, '', window.location.href);
    
    // Instead of navigating back, close the dialog properly
    this.cancelScanning();
  }
  
  private onScroll(): void {
    // Hide scroll hint when user starts scrolling
    if (this.showScrollHint) {
      this.showScrollHint = false;
    }
  }
  
  // Simple touch handlers for mobile
  public handleTouchStart(): void {
    // Not needed for this implementation
  }

  public handleTouchEnd(): void {
    // Not needed for this implementation
  }
  
  public setupCameras(): void {
    WebcamUtil.getAvailableVideoInputs()
      .then((mediaDevices: MediaDeviceInfo[]) => {
        this.availableCameras = mediaDevices || [];
        this.multipleWebcamsAvailable = mediaDevices && mediaDevices.length > 1;
        
        console.log(`Found ${this.availableCameras.length} cameras:`, this.availableCameras);
        
        // If we have cameras, set the first one as default
        if (mediaDevices && mediaDevices.length > 0) {
          // Try to find a back camera for document scanning
          const backCamera = mediaDevices.find(device => 
            device.label?.toLowerCase().includes('back') || 
            device.label?.toLowerCase().includes('environment')
          );
          
          if (backCamera) {
            this.deviceId = backCamera.deviceId;
            console.log('Using back camera:', backCamera.label);
          } else {
            // If no back camera is identified, use the first camera
            this.deviceId = mediaDevices[0].deviceId;
            console.log('Using camera:', mediaDevices[0].label || 'Default camera');
          }
        }
      })
      .catch(err => {
        console.error('Error getting camera devices:', err);
      });
  }
  
  public switchCamera(): void {
    console.log('Switch camera requested');
    
    if (this.multipleWebcamsAvailable && this.availableCameras.length > 1 && !this.isSwitchingCamera) {
      this.isSwitchingCamera = true;
      try {
        // Find the index of the current camera
        const currentIndex = this.availableCameras.findIndex(camera => camera.deviceId === this.deviceId);
        console.log('Current camera index:', currentIndex);
        
        // Get the next camera index (loop back to first if at the end)
        const nextIndex = (currentIndex + 1) % this.availableCameras.length;
        console.log('Next camera index:', nextIndex);
        
        // Update the deviceId to the new camera
        this.deviceId = this.availableCameras[nextIndex].deviceId;
        
        const cameraName = this.availableCameras[nextIndex].label || 'Camera ' + (nextIndex + 1);
        console.log(`Switching to camera: ${cameraName}`);
        
        // Force a refresh of the webcam component
        this.cameraActive = false;
        this.showWebcam = false;
        setTimeout(() => {
          this.showWebcam = true;
          this.isSwitchingCamera = false;
          this.watchForActiveCamera();
        }, 100);
      } catch (error) {
        console.error('Error switching camera:', error);
        this.isSwitchingCamera = false;
      }
    } else {
      console.log('Camera switching not available: Multiple cameras not detected');
    }
  }

  /**
   * Polls for the ngx-webcam <video> element and flips `cameraActive` to true
   * once it is actually playing a stream. While inactive, the template shows an
   * opaque placeholder over the video so the WebView's native play-button
   * overlay (shown for a source-less/paused video) is never visible.
   */
  private watchForActiveCamera(retries = 40): void {
    const video = document.querySelector(
      '.document-scanner-container .webcam-wrapper video',
    ) as HTMLVideoElement | null;

    if (video) {
      const markActive = () => {
        if (video.readyState >= 2 && !video.paused) {
          this.zone.run(() => (this.cameraActive = true));
        }
      };
      if (!video.dataset['readyHooked']) {
        video.dataset['readyHooked'] = '1';
        video.addEventListener('playing', markActive);
        video.addEventListener('loadeddata', markActive);
        video.addEventListener('timeupdate', markActive);
      }
      markActive();
    }

    if (!this.cameraActive && retries > 0) {
      setTimeout(() => this.watchForActiveCamera(retries - 1), 150);
    }
  }

  public triggerSnapshot(): void {
    this.isCapturing = true;
    this.trigger.next();
  }

  public handleInitError(error: WebcamInitError): void {
    this.cameraActive = false;
    this.errors.push(error);
    console.error('Camera error:', error);
  }

  public handleImage(webcamImage: WebcamImage): void {
    this.isCapturing = false;
    this.capturedImages.push(webcamImage);
    console.log('Image captured', this.capturedImages.length);
  }

  public get triggerObservable(): Observable<void> {
    return this.trigger.asObservable();
  }

  public removeImage(index: number): void {
    this.capturedImages.splice(index, 1);
  }

  public cancelScanning(): void {
    this.dialogRef.close();
  }
  
  public completeScanning(): void {
    if (this.capturedImages.length === 0) {
      console.error('No images captured');
      return;
    }

    // Ensure filename has .pdf extension
    const finalFileName = this.fileName.trim() || 'scanned_document';
    const pdfFileName = finalFileName.endsWith('.pdf') ? finalFileName : `${finalFileName}.pdf`;

    this.loadingService.show();
    
    try {
      // Create PDF from images
      this.createPdfFromImages().then((pdfBlob) => {
        try {
          // Convert to File object
          const scannedFile = new File(
            [pdfBlob], 
            pdfFileName, 
            { type: 'application/pdf' }
          );
          
          // Close dialog with the scanned file
          this.dialogRef.close(scannedFile);
          
        } catch (fileError) {
          console.error('Error creating file:', fileError);
        } finally {
          this.loadingService.hide();
        }
      }).catch(error => {
        console.error('Error creating PDF:', error);
        this.loadingService.hide();
      });
    } catch (error) {
      console.error('Error in completeScanning:', error);
      this.loadingService.hide();
    }
  }
  
  private async createPdfFromImages(): Promise<Blob> {
    return new Promise<Blob>((resolve, reject) => {
      try {
        // Create PDF document (A4 format)
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        
        // Use Promise.all to handle all image loading in parallel
        const imagePromises = this.capturedImages.map((webcamImage) => {
          return new Promise<{img: HTMLImageElement, data: string}>((resolveImg, rejectImg) => {
            const img = new Image();
            img.src = webcamImage.imageAsDataUrl;
            
            img.onload = () => {
              resolveImg({
                img: img,
                data: webcamImage.imageAsDataUrl
              });
            };
            
            img.onerror = () => {
              rejectImg(new Error('Failed to load image'));
            };
          });
        });
        
        // When all images are loaded, add them to the PDF
        Promise.all(imagePromises)
          .then(loadedImages => {
            // Process each loaded image
            loadedImages.forEach((loadedImage, index) => {
              // Add a new page for all but the first image
              if (index > 0) {
                pdf.addPage();
              }
              
              const img = loadedImage.img;
              
              // Calculate dimensions to fit page while maintaining aspect ratio
              let imgWidth = pageWidth - 20; // 10mm margin on each side
              let imgHeight = (img.height * imgWidth) / img.width;
              
              // If height exceeds page, scale down
              if (imgHeight > pageHeight - 20) {
                imgHeight = pageHeight - 20;
                imgWidth = (img.width * imgHeight) / img.height;
              }
              
              // Calculate positions to center the image
              const x = (pageWidth - imgWidth) / 2;
              const y = (pageHeight - imgHeight) / 2;
              
              // Add image to PDF
              pdf.addImage(
                loadedImage.data, 
                'JPEG', 
                x, 
                y, 
                imgWidth, 
                imgHeight
              );
            });
            
            // All images have been added to the PDF
            resolve(pdf.output('blob'));
          })
          .catch(error => {
            reject(error);
          });
      } catch (error) {
        reject(error);
      }
    });
  }
}
